import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, AlertTriangle, PauseCircle, Heart } from 'lucide-react';

const CANCEL_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using', label: "I'm not using it enough" },
  { id: 'not_helpful', label: "It's not helping me" },
  { id: 'found_alternative', label: 'Found another solution' },
  { id: 'temporary_break', label: 'Need a temporary break' },
  { id: 'technical_issues', label: 'Technical issues' },
  { id: 'other', label: 'Other reason' }
];

export const CancelSubscriptionModal = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  onPause,
  canceling,
  pausing
}) => {
  const [step, setStep] = useState('choice'); // 'choice', 'pause', 'cancel', 'feedback'
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [wouldReturn, setWouldReturn] = useState(null);
  const [pauseDuration, setPauseDuration] = useState(1);
  const [pauseReason, setPauseReason] = useState('');

  if (!isOpen) return null;

  const handleCancel = () => {
    if (!selectedReason) return;
    onCancel({
      reason: selectedReason,
      feedback: feedback || null,
      would_return: wouldReturn
    });
  };

  const handlePause = () => {
    onPause({
      duration_months: pauseDuration,
      reason: pauseReason || null
    });
  };

  const resetAndClose = () => {
    setStep('choice');
    setSelectedReason('');
    setFeedback('');
    setWouldReturn(null);
    setPauseDuration(1);
    setPauseReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 
              className="text-xl font-medium text-stone-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {step === 'choice' && 'Before you go...'}
              {step === 'pause' && 'Pause Your Journey'}
              {step === 'cancel' && 'We\'re sad to see you go'}
              {step === 'feedback' && 'One last thing'}
            </h2>
            <button 
              onClick={resetAndClose}
              className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              data-testid="close-cancel-modal"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Choice */}
          {step === 'choice' && (
            <div className="space-y-4">
              <p className="text-stone-600">
                Your healing journey matters to us. Would you like to take a break instead of canceling?
              </p>
              
              <div 
                className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50 cursor-pointer hover:border-amber-400 transition-colors"
                onClick={() => setStep('pause')}
                data-testid="choose-pause-option"
              >
                <div className="flex items-start gap-3">
                  <PauseCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-stone-800">Pause my subscription</h3>
                    <p className="text-sm text-stone-600 mt-1">
                      Take 1-3 months off. Your progress and memories with Sage will be saved.
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className="p-4 rounded-xl border-2 border-stone-200 cursor-pointer hover:border-stone-400 transition-colors"
                onClick={() => setStep('cancel')}
                data-testid="choose-cancel-option"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-stone-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-stone-800">Cancel my subscription</h3>
                    <p className="text-sm text-stone-600 mt-1">
                      You'll have access until your current billing period ends.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2a: Pause Options */}
          {step === 'pause' && (
            <div className="space-y-5">
              <p className="text-stone-600">
                How long would you like to pause? Your data and conversation history will be saved.
              </p>

              <div className="space-y-2">
                {[1, 2, 3].map((months) => (
                  <label
                    key={months}
                    className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                      pauseDuration === months 
                        ? 'border-amber-400 bg-amber-50' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pauseDuration"
                      value={months}
                      checked={pauseDuration === months}
                      onChange={() => setPauseDuration(months)}
                      className="sr-only"
                    />
                    <span className="text-stone-800">
                      {months} month{months > 1 ? 's' : ''}
                    </span>
                  </label>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Reason for pausing (optional)
                </label>
                <textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="Taking time to focus on other things..."
                  className="w-full p-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                  rows={2}
                  data-testid="pause-reason-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('choice')}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
                <Button
                  onClick={handlePause}
                  disabled={pausing}
                  className="flex-1 rounded-full text-[#1A1A2E]"
                  style={{ backgroundColor: '#C9A84C' }}
                  data-testid="confirm-pause-button"
                >
                  {pausing ? 'Pausing...' : `Pause for ${pauseDuration} month${pauseDuration > 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2b: Cancel Reason */}
          {step === 'cancel' && (
            <div className="space-y-4">
              <p className="text-stone-600">
                We'd love to understand why you're leaving. This helps us improve.
              </p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {CANCEL_REASONS.map((reason) => (
                  <label
                    key={reason.id}
                    className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                      selectedReason === reason.id 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancelReason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={() => setSelectedReason(reason.id)}
                      className="sr-only"
                    />
                    <span className="text-stone-800 text-sm">{reason.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('choice')}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep('feedback')}
                  disabled={!selectedReason}
                  variant="outline"
                  className="flex-1 rounded-full border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="continue-to-feedback"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Final Feedback */}
          {step === 'feedback' && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-5 h-5 text-rose-400" />
                  <span className="font-medium text-stone-800">Your healing matters</span>
                </div>
                <p className="text-sm text-stone-600">
                  Remember, your journey doesn't end here. You can always return when you're ready.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Any additional feedback? (optional)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share what could have made your experience better..."
                  className="w-full p-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-stone-400"
                  rows={3}
                  data-testid="cancel-feedback-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Would you consider returning in the future?
                </label>
                <div className="flex gap-2">
                  {[
                    { value: true, label: 'Yes, maybe' },
                    { value: false, label: 'Probably not' }
                  ].map((option) => (
                    <button
                      key={String(option.value)}
                      onClick={() => setWouldReturn(option.value)}
                      className={`flex-1 py-2 px-4 rounded-full text-sm border-2 transition-colors ${
                        wouldReturn === option.value
                          ? 'border-stone-800 bg-stone-800 text-white'
                          : 'border-stone-200 text-stone-600 hover:border-stone-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('cancel')}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={canceling}
                  className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-[#1A1A2E]"
                  data-testid="confirm-cancel-button"
                >
                  {canceling ? 'Canceling...' : 'Cancel Subscription'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
